import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import { uploadImage, validateImageFile } from '../lib/storage'
import { useAuth } from '../contexts/AuthContext'

function AddEventModal({ onClose, onSave }) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date_start: '',
    date_end: '',
    date_show: '',
    price: 'Gratuite',
    compte_insta: '',
    alt_text: ''
  })

  const [categories, setCategories] = useState([])
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const typeCategories = [
    { value: 'votive', label: 'Fête Votive' },
    { value: 'petanque', label: 'Pétanque' },
    { value: 'foraine', label: 'Fête Foraine' }
  ]

  const monthCategories = [
    { value: 'juin', label: 'Juin' },
    { value: 'juillet', label: 'Juillet' },
    { value: 'aout', label: 'Août' },
    { value: 'septembre', label: 'Septembre' },
    { value: 'hivernal', label: 'Hivernal (Oct-Mai)' }
  ]

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleCategoryToggle = (categoryValue) => {
    if (categories.includes(categoryValue)) {
      setCategories(categories.filter(c => c !== categoryValue))
    } else {
      setCategories([...categories, categoryValue])
    }
  }

  const handleMonthSelect = (monthValue) => {
    // Retirer tous les mois existants et ajouter le nouveau
    const monthValues = monthCategories.map(m => m.value)
    const otherCategories = categories.filter(c => !monthValues.includes(c))
    setCategories([...otherCategories, monthValue])
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      validateImageFile(file)
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
      setError('')
    } catch (err) {
      setError(err.message)
      setImageFile(null)
      setImagePreview(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (!imageFile) {
        throw new Error('Veuillez ajouter une affiche')
      }

      if (categories.length === 0) {
        throw new Error('Veuillez sélectionner au moins une catégorie')
      }

      // Upload de l'image
      const { url: imageUrl, path: imagePath } = await uploadImage(imageFile, 'events')

      // Créer le slug
      const slug = formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')

      // Créer l'événement
      const { error: insertError } = await supabase
        .from('events')
        .insert([{
          title: formData.title,
          slug: slug,
          description: formData.description,
          date_start: formData.date_start,
          date_end: formData.date_end,
          date_show: formData.date_show,
          price: formData.price,
          categories: categories,
          compte_insta: formData.compte_insta,
          image_url: imageUrl,
          image_path: imagePath,
          alt_text: formData.alt_text || formData.title,
          status: 'published',
          created_by: user.id
        }])

      if (insertError) throw insertError

      onSave()
      onClose()
    } catch (err) {
      console.error('Erreur lors de la création:', err)
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8">
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Ajouter un événement</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Titre */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Titre de l'événement *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="date_start" className="block text-sm font-medium text-gray-700 mb-2">
                  Date de début *
                </label>
                <input
                  type="datetime-local"
                  id="date_start"
                  name="date_start"
                  value={formData.date_start}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="date_end" className="block text-sm font-medium text-gray-700 mb-2">
                  Date de fin *
                </label>
                <input
                  type="datetime-local"
                  id="date_end"
                  name="date_end"
                  value={formData.date_end}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Date à afficher */}
            <div>
              <label htmlFor="date_show" className="block text-sm font-medium text-gray-700 mb-2">
                Date à afficher *
              </label>
              <input
                type="text"
                id="date_show"
                name="date_show"
                value={formData.date_show}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ex: Samedi 15 Juillet 2025 | Bal dès 21h"
              />
            </div>

            {/* Prix */}
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                Prix
              </label>
              <input
                type="text"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Types d'événement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Types d'événement * (plusieurs choix possibles)
              </label>
              <div className="flex flex-wrap gap-2">
                {typeCategories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleCategoryToggle(cat.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      categories.includes(cat.value)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mois */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mois * (un seul choix)
              </label>
              <div className="flex flex-wrap gap-2">
                {monthCategories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleMonthSelect(cat.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      categories.includes(cat.value)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Affiche de l'événement * (JPG, PNG ou WebP, max 5 Mo)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              {imagePreview && (
                <div className="mt-4">
                  <img src={imagePreview} alt="Aperçu" className="max-w-xs rounded border" />
                </div>
              )}
            </div>

            {/* Instagram */}
            <div>
              <label htmlFor="compte_insta" className="block text-sm font-medium text-gray-700 mb-2">
                Compte Instagram (optionnel)
              </label>
              <input
                type="url"
                id="compte_insta"
                name="compte_insta"
                value={formData.compte_insta}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="https://www.instagram.com/votre_compte/"
              />
            </div>

            {/* Boutons */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Création...' : 'Créer l\'événement'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddEventModal
